<template>
  <div>
    <p>{{ $t("节点配置") }}</p>

    <div class="condition-config">
      <t-form-item
        :name="formData.matchMode"
        :status-icon="false"
        style="margin-bottom: 10px"
      >
        <t-radio-group v-model="formData.matchMode" @change="onMatchModeChange">
          <t-radio :value="1">{{ $t("AI智能判断") }}</t-radio>
          <t-radio :value="2">{{ $t("手动添加规则") }}</t-radio>
        </t-radio-group>
      </t-form-item>

      <!-- AI智能判断模式 -->
      <template v-if="formData.matchMode === MatchMode.AI">
        <div
          v-for="(branch, branchIndex) in formData.branches.filter(
            (v) => v.mode !== BranchMode.ELSE,
          )"
          :key="branch.id"
        >
          <t-card class="ai-branch-card">
            <t-form-item
              :name="`branches[${branchIndex}].conditions[0].content`"
              :rules="aiConditionRequireRule"
              :required-mark="false"
            >
              <template #label>
                <div
                  class="ai-branch-header"
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                  "
                >
                  <span><text-with-mark :text="$t('条件描述')" /></span>
                  <CloseCircleIcon
                    v-if="formData.branches.length > 1"
                    size="16"
                    style="cursor: pointer; margin-left: auto"
                    @click="deleteAIBranch(branchIndex)"
                  />
                </div>
              </template>
              <variable-rich-editor
                v-model="branch.conditions[0].content"
                :variables="variables"
                @change="handleEditorChange"
              />
            </t-form-item>
          </t-card>
        </div>
        <!-- 与其他条件一起一共最多11个条件 -->
        <t-form-item v-if="formData.branches.length < 11" :status-icon="false">
          <t-link theme="primary" hover="color" @click="addAIBranch">
            {{ $t("添加条件") }}
          </t-link>
        </t-form-item>
      </template>

      <!-- 手动添加规则模式 -->
      <template v-else>
        <div
          v-for="(item, index) in formData.branches.filter(
            (v) => v.mode !== BranchMode.ELSE,
          )"
          :key="index"
        >
          <t-form-item>
            <t-card :value="`${index}`">
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 10px;
                "
              >
                <span style="color: #6d778c">{{
                  $t("条件组") + " " + (index + 1)
                }}</span>
                <CloseCircleIcon
                  :disabled="formData.branches.length <= 1"
                  size="16"
                  style="padding-left: 8px; vertical-align: sub"
                  @click="deleteConditionGroup(index)"
                />
              </div>

              <t-form-item
                v-if="formData.branches[index].conditions?.length > 1"
                :name="`branches[${index}].mode`"
                :status-icon="false"
                :rules="requireRule"
                :required-mark="false"
              >
                <t-radio-group
                  :value="formData.branches[index].mode"
                  style="margin-bottom: 10px"
                  @change="onTypeChange(index, $event)"
                >
                  <t-radio :value="BranchMode.AND">{{ $t("并且") }}</t-radio>
                  <t-radio :value="BranchMode.OR">{{ $t("或者") }}</t-radio>
                </t-radio-group>
              </t-form-item>

              <div
                v-for="(conditionItem, itemIndex) in formData.branches[index]
                  .conditions"
                :key="itemIndex"
              >
                <t-divider v-if="itemIndex > 0" align="left">
                  <t-tag theme="success" shape="round" color="#00B89C">
                    {{
                      formData.branches[index].mode === BranchMode.OR
                        ? $t("或者")
                        : $t("并且")
                    }}
                  </t-tag>
                </t-divider>
                <div class="condition">
                  <div>
                    <div>
                      <t-space :size="8">
                        <t-form-item
                          :name="`branches[${index}].conditions[${itemIndex}].info_id`"
                          :rules="requireRule"
                          :required-mark="false"
                          style="margin-bottom: 8px"
                        >
                          <rule-variable-selector
                            v-model="
                              formData.branches[index].conditions[itemIndex]
                                .info_id
                            "
                            :variables="ruleVariables"
                            @change="
                              (value) => onInfoIdChange(index, itemIndex, value)
                            "
                          />
                        </t-form-item>
                        <t-form-item
                          :name="`branches[${index}].conditions[${itemIndex}].mode`"
                          :rules="requireRule"
                          :required-mark="false"
                        >
                          <t-select
                            :options="relationOptions"
                            :value="
                              formData.branches[index].conditions[itemIndex]
                                .mode
                            "
                            style="width: 157px"
                            @change="onRelationChange(index, itemIndex, $event)"
                          />
                        </t-form-item>
                      </t-space>
                    </div>
                    <div
                      v-if="
                        ![
                          ConditionMode.IS_NULL,
                          ConditionMode.NOT_NULL,
                        ].includes(
                          formData.branches[index].conditions[itemIndex].mode,
                        )
                      "
                      style="margin-top: 10px"
                    >
                      <t-form-item
                        :name="`branches[${index}].conditions[${itemIndex}].content`"
                        :rules="requireRule"
                        :required-mark="false"
                      >
                        <t-input
                          v-model="
                            formData.branches[index].conditions[itemIndex]
                              .content
                          "
                          maxlength="100"
                          style="width: 358px"
                          :placeholder="$t('填写常量')"
                        />
                      </t-form-item>
                    </div>
                  </div>
                  <div>
                    <CloseCircleIcon
                      v-if="formData.branches[index].conditions.length > 1"
                      size="16"
                      style="cursor: pointer"
                      @click="deleteCondition(index, itemIndex)"
                    />
                  </div>
                </div>
              </div>

              <t-form-item
                v-if="formData.branches[index].conditions.length < 10"
                :status-icon="false"
                style="padding-top: 8px"
              >
                <t-link
                  theme="primary"
                  hover="color"
                  @click="addCondition(index)"
                >
                  {{ $t("添加条件") }}
                </t-link>
              </t-form-item>
            </t-card>
          </t-form-item>
        </div>
        <!-- 与其他条件一起一共最多11个条件 -->
        <t-form-item v-if="formData.branches.length < 11" :status-icon="false">
          <t-space size="small">
            <t-link theme="primary" hover="color" @click="addConditionGroup">
              {{ $t("添加条件组") }}
            </t-link>
          </t-space>
        </t-form-item>
      </template>
    </div>
    <t-card>
      {{ $t("其他") }}
      <text-with-mark
        :hidden-mark="true"
        :text="$t('（当以上条件都不符合时，进入该条件）')"
      />
    </t-card>
  </div>
</template>

<script lang="ts" setup>
import { reactive, ref, watch, inject, toRaw, Ref } from "vue";
import { CloseCircleIcon } from "tdesign-icons-vue-next";
import { map, merge, trim, cloneDeep } from "lodash-es";
import { getRelationUUID } from "@/utils";
import { t } from "@/utils/i18n";
import { systemVarsPool } from "@/utils/const";
import { extractInfoNodesData } from "@/flow/flow-util/node/utils";
import {
  ConditionMode,
  MatchMode,
  BranchMode,
  Branch,
  ConditionJudgeData,
  Variable,
  InfoOption,
  JudgeType,
} from "@/types/conditionJudgeNodeType";
import { formatContentWithLabels } from "@/flow/flow-util/node/node-utils/formatContentWithLabels";
import RuleVariableSelector from "@/components/variable-rich-editor/rule-variable-selector.vue";

const emit = defineEmits(["updateData"]);

const relationOptions = [
  { label: t("包含"), value: ConditionMode.CONTAIN },
  { label: t("不包含"), value: ConditionMode.NOT_CONTAIN },
  { label: t("等于"), value: ConditionMode.EQUAL },
  { label: t("不等于"), value: ConditionMode.NOT_EQUAL },
  { label: t("为空"), value: ConditionMode.IS_NULL },
  { label: t("不为空"), value: ConditionMode.NOT_NULL },
];

const requireRule = [
  {
    validator: (str) => {
      return !!trim(str);
    },
    message: t("不能为空"),
    trigger: "change",
  },
];

const props = defineProps<{
  data: ConditionJudgeData;
}>();

const formData = reactive<ConditionJudgeData>({
  matchMode: props.data.matchMode || MatchMode.AI,
  branches: cloneDeep(props.data.branches) || [],
});

const elseBranch = {
  id: getRelationUUID(),
  name: "其他",
  content: "其他",
  mode: BranchMode.ELSE,
  conditions: [
    {
      content: "",
    },
  ],
} as Branch;

const oldBranches: Record<MatchMode, Branch[]> = {
  [MatchMode.AI]: [],
  [MatchMode.RULE]: [],
};
const onMatchModeChange = (value: MatchMode) => {
  const oldBranch = oldBranches[value];

  // 保存旧分支
  const oldMatchMode = value == MatchMode.AI ? MatchMode.RULE : MatchMode.AI;
  const rawBranches = toRaw(formData.branches);
  oldBranches[oldMatchMode] = cloneDeep(rawBranches);

  if (oldBranch.length) {
    formData.branches = oldBranch;
    return;
  }

  if (value === MatchMode.AI) {
    // 切换到AI模式时，添加一个默认分支
    formData.branches = [
      {
        id: getRelationUUID(),
        name: t("分支"),
        mode: BranchMode.AI,
        conditions: [
          {
            type: JudgeType.INFO,
            info_id: "",
            mode: ConditionMode.CONTAIN,
            content: "",
          },
        ],
      },
      elseBranch,
    ];
  } else {
    // 切换到规则模式时，添加一个默认的规则条件组
    formData.branches = [
      {
        id: getRelationUUID(),
        name: t("条件组") + " 1",
        mode: BranchMode.AND, // 默认使用"并且"
        conditions: [
          {
            type: JudgeType.INFO,
            info_id: "",
            mode: ConditionMode.EQUAL, // 默认使用"等于"
            content: "",
          },
        ],
      },
      elseBranch,
    ];
  }
};

const onRelationChange = (
  index: number,
  itemIndex: number,
  mode: ConditionMode,
) => {
  formData.branches[index].conditions[itemIndex].mode = mode;
  if (mode === ConditionMode.IS_NULL || mode === ConditionMode.NOT_NULL) {
    // 为空或不为空时，清空values
    formData.branches[index].conditions[itemIndex].content = "";
  } else if (!formData.branches[index].conditions[itemIndex].content) {
    // 其他情况如果没有values，初始化为空数组
    formData.branches[index].conditions[itemIndex].content = "";
  }
};

const addConditionGroup = () => {
  // 找到 else 分支并过滤出其他分支
  const elseBranch = formData.branches.find((v) => v.mode === BranchMode.ELSE);
  const normalBranches = formData.branches.filter(
    (v) => v.mode !== BranchMode.ELSE,
  );

  // 添加新的条件组
  const newBranch = {
    id: getRelationUUID(),
    name: t("条件组") + " " + (normalBranches.length + 1),
    mode: BranchMode.AND,
    conditions: [
      {
        type: JudgeType.INFO,
        info_id: "",
        mode: ConditionMode.CONTAIN,
        content: "",
      },
    ],
  };

  // 重新组装分支数组，确保 else 分支在最后
  formData.branches = [...normalBranches, newBranch, elseBranch].filter(
    (branch): branch is Branch => Boolean(branch),
  );
};

const deleteConditionGroup = (index: number) => {
  if (formData.branches.length <= 2) return;
  // 找到要删除的分支
  const branchToDelete = formData.branches[index];
  // 如果是 else 分支则不允许删除
  if (branchToDelete.mode === BranchMode.ELSE) return;
  formData.branches.splice(index, 1);
};

const deleteCondition = (index: number, itemIndex: number) => {
  if (formData.branches[index].conditions.length <= 1) return;
  formData.branches[index].conditions.splice(itemIndex, 1);
};

const addCondition = (index: number) => {
  formData.branches[index].conditions.push({
    type: JudgeType.INFO,
    info_id: "",
    mode: ConditionMode.CONTAIN,
    content: "",
  });
};

const onTypeChange = (index: number, value: BranchMode) => {
  formData.branches[index].mode = value;
};

// 当选择info_id时，根据选项来源更新condition的type值
const onInfoIdChange = (index: number, itemIndex: number, infoId: string) => {
  if (!infoId) return;

  // 从 variables 中查找对应的选项，确定来源类型
  const findTypeInVariables = (
    vars: Variable[],
    targetValue: string,
  ): JudgeType | null => {
    for (const v of vars) {
      // 如果是系统变量组，检查 children
      if (v.value === JudgeType.SYSTEM && v.children) {
        const matched = v.children.find((child) => child.value === targetValue);
        if (matched) {
          return JudgeType.SYSTEM;
        }
      }
      // 如果是信息收集节点，检查 children
      if (v.type === JudgeType.INFO && v.children) {
        const matched = v.children.find((child) => child.value === targetValue);
        if (matched) {
          return JudgeType.INFO;
        }
      }
      // 如果是接口调用节点，检查 children
      if (v.type === JudgeType.CUSTOM && v.children) {
        const matched = v.children.find((child) => child.value === targetValue);
        if (matched) {
          return JudgeType.CUSTOM;
        }
      }
      // 递归查找 children
      if (v.children) {
        const found = findTypeInVariables(v.children, targetValue);
        if (found) return found;
      }
    }
    return null;
  };

  // 手动添加规则模式使用 ruleVariables（value格式为纯ID），AI智能判断模式使用 variables（value格式为${3-xxx}）
  // 由于 onInfoIdChange 只在手动添加规则模式下调用，所以使用 ruleVariables
  const sourceType = findTypeInVariables(ruleVariables.value, infoId);
  if (sourceType) {
    // 根据来源类型设置condition的type值
    // 信息收集节点: type = 1 (JudgeType.INFO)
    // 接口调用节点: type = 3 (JudgeType.CUSTOM)
    // 系统变量: type = 2 (JudgeType.SYSTEM)
    formData.branches[index].conditions[itemIndex].type = sourceType;
  }
};

const addAIBranch = () => {
  // 找到 else 分支并过滤出其他分支
  const elseBranch = formData.branches.find((v) => v.mode === BranchMode.ELSE);
  const normalBranches = formData.branches.filter(
    (v) => v.mode !== BranchMode.ELSE,
  );

  // 添加新的 AI 分支
  const newBranch = {
    id: getRelationUUID(),
    name: t("分支"),
    mode: BranchMode.AI,
    conditions: [
      {
        type: JudgeType.INFO,
        info_id: "",
        mode: ConditionMode.CONTAIN,
        content: "",
      },
    ],
  };

  // 重新组装分支数组，确保 else 分支在最后
  formData.branches = [...normalBranches, newBranch, elseBranch].filter(
    (branch): branch is Branch => Boolean(branch),
  );
};

const deleteAIBranch = (index: number) => {
  if (formData.branches.length <= 2) return;
  // 找到要删除的分支
  const branchToDelete = formData.branches[index];
  // 如果是 else 分支则不允许删除
  if (branchToDelete.mode === BranchMode.ELSE) return;
  formData.branches.splice(index, 1);
};

// 变量列表
const variables = ref<Variable[]>([]);
// 手动添加规则模式专用的变量列表（接口调用节点的value使用纯ID格式）
const ruleVariables = ref<Variable[]>([]);
const variablePool = inject<Ref<string[]>>("variablePool", ref<string[]>([]));
const xGraph = inject("xGraph");
const infosOptions = ref<InfoOption[]>([]);
// 变量ID映射列表（智能任务流使用）
const varIdList = inject<
  Ref<Array<{ id: string; nodeId: string; varName: string }>>
>("varIdList", ref([]));
watch(
  xGraph as any,
  (newVal) => {
    const systemVars = systemVarsPool.map((v) => {
      return {
        value: `\${${JudgeType.SYSTEM}-${v.value}}`,
        label: v.label,
        type: JudgeType.SYSTEM,
      };
    });

    const nodes = newVal.getNodes();
    // 信息收集节点：使用节点id作为value
    const infoVars = extractInfoNodesData(nodes)
      .map((v) => {
        // 过滤掉 label 为空（info.name 为空）的 children
        const validChildren = v.infos
          .filter((info) => info.name && info.name.trim())
          .map((info) => ({
            value: info.value,
            label: info.name,
            type: JudgeType.INFO,
            id: info.id,
          }));

        return {
          value: v.nodeId, // 使用节点id作为value
          type: JudgeType.INFO,
          label: v.nodeName,
          nodeId: v.nodeId, // 保存节点id
          children: validChildren,
        };
      })
      .filter((v) => {
        // 如果过滤后 children 为空，则过滤掉整个信息收集节点
        if (!v.children || v.children.length === 0) {
          return false;
        }
        return true;
      });

    // 收集接口调用节点的输出参数（returns）
    const apiCallNodesData: Array<{
      nodeId: string;
      nodeName: string;
      returns: Array<{ variable_id: string; key: string }>;
    }> = [];
    nodes.forEach((node) => {
      const nodeModel = node.getModel ? node.getModel() : node;
      const nodeData = nodeModel.nodeData;
      // 收集所有接口调用节点的输出参数
      if (
        nodeData.nodeType === "interfaceInvocationNode" &&
        nodeData.returns &&
        Array.isArray(nodeData.returns)
      ) {
        apiCallNodesData.push({
          nodeId: nodeModel.id || nodeData.id, // 使用节点id
          nodeName: nodeData.nodeName || "",
          returns: nodeData.returns.filter(
            (r) =>
              r.variable_id && r.key && r.variable_id.startsWith("userdefine-"),
          ),
        });
      }
    });

    // 将接口调用节点的输出参数转换为变量格式：使用节点id作为value
    // 从 varIdList 获取最新的变量名（响应式更新）
    const apiCallVars = apiCallNodesData
      .map((node) => ({
        value: node.nodeId, // 使用节点id作为value
        type: JudgeType.CUSTOM,
        label: node.nodeName,
        nodeId: node.nodeId, // 保存节点id
        children: node.returns.map((ret) => {
          // 从 varIdList 中获取最新的变量名
          const varInfo = varIdList.value.find((v) => v.id === ret.variable_id);
          const varName = varInfo ? varInfo.varName : ret.key;

          return {
            value: `\${${JudgeType.CUSTOM}-${ret.variable_id}}`,
            label: varName, // 使用最新的变量名
            type: JudgeType.CUSTOM,
            id: ret.variable_id,
          };
        }),
      }))
      .filter((v) => {
        // 过滤掉 type 为 3 且 children 长度为 0 的节点
        if (
          v.type === JudgeType.CUSTOM &&
          (!v.children || v.children.length === 0)
        ) {
          return false;
        }
        return true;
      });

    // AI智能判断模式：只显示系统变量、信息收集节点、接口调用节点，不显示自定义变量
    variables.value = [
      {
        value: JudgeType.SYSTEM,
        label: t("系统变量"),
        type: "system",
        children: systemVars,
      },
      ...infoVars,
      ...apiCallVars,
    ];

    // 构建手动添加规则模式专用的变量列表
    // 系统变量的children的value使用纯值格式（v.value），而不是${2-xxx}格式
    const systemVarsForRule = systemVarsPool.map((v) => {
      return {
        value: v.value, // 使用纯值格式，而不是${2-xxx}格式
        label: v.label,
        type: JudgeType.SYSTEM,
      };
    });

    // 接口调用节点的children的value使用纯ID格式（ret.variable_id），而不是${3-xxx}格式
    const apiCallVarsForRule = apiCallNodesData
      .map((node) => ({
        value: node.nodeId,
        type: JudgeType.CUSTOM,
        label: node.nodeName,
        nodeId: node.nodeId,
        children: node.returns.map((ret) => {
          const varInfo = varIdList.value.find((v) => v.id === ret.variable_id);
          const varName = varInfo ? varInfo.varName : ret.key;

          return {
            value: ret.variable_id, // 使用纯ID格式，而不是${3-xxx}格式
            label: varName,
            type: JudgeType.CUSTOM,
            id: ret.variable_id,
          };
        }),
      }))
      .filter((v) => {
        if (
          v.type === JudgeType.CUSTOM &&
          (!v.children || v.children.length === 0)
        ) {
          return false;
        }
        return true;
      });

    // 手动添加规则模式：使用纯ID格式的变量列表
    ruleVariables.value = [
      {
        value: JudgeType.SYSTEM,
        label: t("系统变量"),
        type: "system",
        children: systemVarsForRule,
      },
      ...infoVars,
      ...apiCallVarsForRule,
    ];

    // 构建 infosOptions，先添加系统变量，然后包含信息提取节点和接口调用节点的输出参数
    // 使用已经过滤过的 infoVars 和 apiCallVars（它们已经应用了过滤逻辑）
    const systemOptions = systemVarsPool.map((v) => ({
      value: `\${${JudgeType.SYSTEM}-${v.value}}`,
      label: t("系统变量") + "/" + v.label, // 系统变量标签前面加上"系统变量/"
      childLabel: v.label,
      tip: v.tip || v.label,
      showButton: false,
      sourceType: JudgeType.SYSTEM, // 系统变量，type = 2
    }));

    const infoExtractOptions = infoVars
      .map((info) => {
        const { label, children } = info;
        return children.map((child) => ({
          value: child.id,
          label: label + "/" + child.label,
          childLabel: child.label,
          tip: label,
          showButton: true,
          sourceType: JudgeType.INFO, // 信息收集节点，type = 1
        }));
      })
      .flat();

    // 使用已经过滤过的 apiCallVars（已经过滤掉 children 为空的节点）
    const apiCallOptions = apiCallVars
      .map((node) => {
        return node.children.map((child) => ({
          value: child.id, // 使用 variable_id
          label: node.label + "/" + child.label, // 节点名/变量名
          childLabel: child.label,
          tip: node.label,
          showButton: false,
          sourceType: JudgeType.CUSTOM, // 接口调用节点，type = 3
        }));
      })
      .flat();

    // 先添加系统变量，然后添加信息收集节点和接口调用节点
    infosOptions.value = [
      ...systemOptions,
      ...infoExtractOptions,
      ...apiCallOptions,
    ];
  },
  { immediate: true },
);

// 监听 varIdList 变化，清理已删除变量的引用
watch(
  varIdList,
  (newList) => {
    // 只有当 varIdList 不为空时才执行清理逻辑，避免初始化阶段误清空
    if (!newList || newList.length === 0) {
      return;
    }

    const validVariableIds = new Set(newList.map((v) => v.id));

    // 遍历所有分支的条件
    formData.branches.forEach((branch) => {
      if (branch.conditions && Array.isArray(branch.conditions)) {
        branch.conditions.forEach((condition) => {
          // 检查条件中引用的 info_id 是否是自定义变量
          if (condition.info_id && condition.info_id.includes("${3-")) {
            // 提取 variable_id: ${3-userdefine-xxx} -> userdefine-xxx
            const match = condition.info_id.match(/\$\{3-([^}]+)\}/);
            if (match) {
              const variableId = match[1];
              // 如果该变量已被删除，清空引用
              if (!validVariableIds.has(variableId)) {
                console.log("清理条件判断中已删除变量的引用:", variableId);
                condition.info_id = "";
              }
            }
          }
        });
      }
    });
  },
  { deep: true },
);

const aiConditionRequireRule = [
  {
    validator: (str) => {
      const { display } = formatContentWithLabels(str, {
        infoNodes: [
          {
            infos: infosOptions.value.map((v) => ({
              id: v.value,
              name: v.childLabel || v.label,
            })),
          },
        ],
        systemVars: systemVarsPool,
        customVars: variablePool.value.map((v) => ({
          value: v,
          label: v,
          type: JudgeType.CUSTOM,
        })),
        returnWithBrackets: true,
      });
      return display.length < 200;
    },
    message: t("200字以内"),
  },
  {
    validator: (str) => {
      return !!trim(str);
    },
    message: t("不能为空"),
    trigger: "change",
  },
];

watch(
  variablePool as any,
  (newVal) => {
    const customVars = newVal.map((v) => {
      return {
        value: `\${${JudgeType.CUSTOM}-${v}}`,
        label: v,
        type: JudgeType.CUSTOM,
      };
    });
    const customVar = variables.value.find(
      (v) => v.value == `${JudgeType.CUSTOM}`,
    );
    if (customVar) {
      customVar.children = customVars;
    }
  },
  { immediate: true },
);

// 处理编辑器内容变化
const handleEditorChange = ({ html, text, content }) => {
  console.log("content:", content);
};

watch(formData, (newVal) => {
  emit(
    "updateData",
    merge({}, newVal, {
      matchMode: newVal.matchMode,
      branches: map(
        // 先过滤出非 else 分支,然后添加 else 分支到最后
        newVal.branches,
        (v, index) => {
          if (v?.mode === BranchMode.ELSE) {
            return {
              ...v,
              name: t("其他"),
              content: t("其他"),
            };
          }
          return {
            ...v,
            name: t("条件组") + " " + (index + 1),
            content: t("条件组") + " " + (index + 1),
          };
        },
      ),
    }),
  );
});
</script>
<style lang="less" scoped>
:deep(.flow-t-card) {
  width: 100%;
  background-color: #f9fafc;
  margin-bottom: 15px;
  border-radius: 8px;
  border: 1px solid #e4e8ee;

  :deep(.flow-t-form__controls-content) {
    display: none;
  }
}
.ai-branch-card {
  :deep(.flow-t-card__body) {
    padding-bottom: 22px;
  }
}
:deep(.flow-t-divider--with-text-left::before) {
  width: 0;
}
:deep(.flow-t-button--variant-text:hover) {
  border-color: transparent;
  background-color: transparent;
}
:deep(.flow-t-form__label) {
  padding-right: 0px;
}
:deep(.flow-t-form__controls .flow-t-form__item) {
  margin-right: 0px;
}
.condition-config {
  margin-top: 16px;
}
.condition {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
